// AdminServiceBookingsTab — admin/super-admin view of partner service bookings.
// Sibling to AdminBookingsTab (event tickets). Allows filtering and manual
// status transitions (mark completed / cancelled / refunded after coordinating
// with the partner).
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import AppModal from '../ui/AppModal';

const STATUS_COLORS = {
  pending:           'bg-amber-100 text-amber-700',
  paid:              'bg-blue-100 text-blue-700',
  enquiry_received:  'bg-purple-100 text-purple-700',
  completed:         'bg-green-100 text-green-700',
  cancelled:         'bg-red-100 text-red-700',
  refunded:          'bg-gray-100 text-gray-500',
};

const STATUS_FILTERS = [
  { value: '',                  label: 'All' },
  { value: 'pending',           label: 'Pending' },
  { value: 'paid',              label: 'Paid' },
  { value: 'enquiry_received',  label: 'Enquiries' },
  { value: 'completed',         label: 'Completed' },
  { value: 'cancelled',         label: 'Cancelled' },
  { value: 'refunded',          label: 'Refunded' },
];

const fmtINR = (n) =>
  n == null
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN')}`;

const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return s;
  }
};

export default function AdminServiceBookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionStatus, setActionStatus] = useState('completed');
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const r = await axiosInstance.get('/admin/service-bookings', { params });
      setBookings(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load service bookings');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus]);

  const filtered = bookings.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (b.service_name || '').toLowerCase().includes(q) ||
      (b.partner_name || '').toLowerCase().includes(q) ||
      (b.user_name    || '').toLowerCase().includes(q) ||
      (b.user_email   || '').toLowerCase().includes(q) ||
      (b.contact_phone|| '').toLowerCase().includes(q) ||
      (b.id           || '').toLowerCase().includes(q)
    );
  });

  const totals = {
    count: filtered.length,
    revenue: filtered
      .filter((b) => b.status === 'paid' || b.status === 'completed')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0),
  };

  const openAction = (booking, defaultStatus) => {
    setActionTarget(booking);
    setActionStatus(defaultStatus);
    setActionNote('');
  };

  const submitAction = async () => {
    if (!actionTarget) return;
    setSubmitting(true);
    try {
      await axiosInstance.patch(`/admin/service-bookings/${actionTarget.id}`, {
        status: actionStatus,
        admin_note: actionNote.trim() || null,
      });
      toast.success(`Marked as ${actionStatus}`);
      setActionTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-service-bookings-tab">
      {/* Filter strip */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setFilterStatus(f.value)}
            data-testid={`svc-booking-filter-${f.value || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${
              filterStatus === f.value
                ? 'bg-[#2B3A67] text-white'
                : 'bg-white border border-gray-100 text-gray-500'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <input
        data-testid="svc-booking-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search service / partner / user / phone / id"
        className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 text-sm focus:border-[#2B3A67] focus:outline-none"
      />

      {/* Stats strip */}
      {!loading && (
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="bg-white rounded-2xl py-3 border border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Bookings
            </p>
            <p className="text-2xl font-extrabold text-[#2B3A67]" data-testid="svc-booking-count">
              {totals.count}
            </p>
          </div>
          <div className="bg-white rounded-2xl py-3 border border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Revenue (paid+done)
            </p>
            <p className="text-2xl font-extrabold text-[#E54562]" data-testid="svc-booking-revenue">
              {fmtINR(totals.revenue)}
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm">
          {search || filterStatus ? 'No bookings match this filter.' : 'No service bookings yet.'}
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-service-bookings-list">
          {filtered.map((b) => (
            <div
              key={b.id}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
              data-testid={`svc-booking-row-${b.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-extrabold truncate">{b.service_name}</p>
                  <p className="text-xs text-gray-400">
                    {b.partner_name || 'Unknown partner'} · {b.variant?.label || '—'}
                  </p>
                </div>
                <span
                  className={`text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider whitespace-nowrap ${
                    STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'
                  }`}
                  data-testid={`svc-booking-status-${b.id}`}
                >
                  {b.status.replace('_', ' ')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="text-gray-400">Customer</span>
                  <p className="font-bold truncate" title={b.user_email}>
                    {b.user_name || '—'}
                  </p>
                  <p className="text-gray-500 truncate">{b.user_email}</p>
                </div>
                <div>
                  <span className="text-gray-400">Phone</span>
                  <p className="font-bold">{b.contact_phone || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Date / Slot</span>
                  <p className="font-bold">{b.preferred_date} · {b.preferred_slot}</p>
                </div>
                <div>
                  <span className="text-gray-400">City</span>
                  <p className="font-bold">{b.city}</p>
                </div>
                <div>
                  <span className="text-gray-400">Amount</span>
                  <p className="font-bold text-[#E54562]">
                    {b.is_combo_enquiry ? 'Quote pending' : fmtINR(b.total_amount)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Payment</span>
                  <p className="font-bold capitalize">{b.payment_method}</p>
                </div>
              </div>

              {b.notes && (
                <div className="mt-2 text-xs bg-gray-50 rounded-lg p-2 whitespace-pre-line">
                  <span className="text-gray-400 font-bold">Notes: </span>
                  {b.notes}
                </div>
              )}

              <div className="text-[10px] text-gray-400 mt-2">
                #{b.id.slice(0, 8)} · {fmtDate(b.created_at)}
              </div>

              {/* Admin actions — only for in-flight bookings */}
              {!['completed', 'cancelled', 'refunded'].includes(b.status) && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openAction(b, 'completed')}
                    className="flex-1 px-3 py-2 border-2 border-emerald-500 text-emerald-600 font-bold text-xs rounded-xl"
                    data-testid={`svc-booking-complete-${b.id}`}
                  >
                    Mark Completed
                  </button>
                  <button
                    onClick={() => openAction(b, 'cancelled')}
                    className="flex-1 px-3 py-2 border-2 border-amber-500 text-amber-600 font-bold text-xs rounded-xl"
                    data-testid={`svc-booking-cancel-${b.id}`}
                  >
                    Cancel
                  </button>
                  {b.status === 'paid' && (
                    <button
                      onClick={() => openAction(b, 'refunded')}
                      className="flex-1 px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl"
                      data-testid={`svc-booking-refund-${b.id}`}
                    >
                      Refund
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action modal */}
      <AppModal
        open={Boolean(actionTarget)}
        onClose={() => setActionTarget(null)}
        title={`Mark booking as ${actionStatus}`}
      >
        {actionTarget && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <p className="font-bold">{actionTarget.service_name}</p>
              <p className="text-xs text-gray-500">
                {actionTarget.user_name} · {actionTarget.preferred_date} · {actionTarget.preferred_slot}
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                Note (optional, sent to customer)
              </label>
              <textarea
                data-testid="svc-booking-action-note"
                rows={3}
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={
                  actionStatus === 'completed'
                    ? 'e.g. Thanks for visiting!'
                    : actionStatus === 'cancelled'
                    ? 'e.g. Spa closed unexpectedly that day'
                    : 'e.g. Refund processed via Razorpay'
                }
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActionTarget(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl"
              >
                Cancel
              </button>
              <button
                onClick={submitAction}
                disabled={submitting}
                data-testid="svc-booking-action-submit"
                className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50"
              >
                {submitting ? 'Saving...' : `Confirm ${actionStatus}`}
              </button>
            </div>
          </div>
        )}
      </AppModal>
    </div>
  );
}
